import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Theme modes are an open registry: any mode can be added at runtime via
 * `registerThemeMode` (or by extending DEFAULT_THEME_MODES), so the project
 * is not limited to light/dark.
 */
export type ThemeMode = string;

export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export interface PlanPageThemeColors {
  primary: string;
  primaryHover: string;
  primaryPressed: string;
  textDark: string;
  textSecondary: string;
  border: string;
  surface: string;
  background: string;
  activeBg: string;
  success: string;
  danger: string;
  grey: string;
  white: string;
  black: string;
}

/**
 * Plan Page UI revamp theme properties.
 * Sources:
 * - Page design Figma (node 74137-1394): Primary #8B5CF6, Black #121826, light grey #D9D9D9
 * - Brand guidelines Figma (node 73900-838): Lavender Tint #F5F0FF, Success #008130,
 *   Alert #E53E3E, Grey #818181, Black #000000, White #FFFFFF
 */
const LIGHT_COLORS: PlanPageThemeColors = {
  primary: '#8B5CF6',
  primaryHover: '#7C3AED',
  primaryPressed: '#6D28D9',
  textDark: '#121826',
  textSecondary: '#708090',
  border: '#E4D5FC',
  surface: '#FFFFFF',
  background: '#F7F7FB',
  activeBg: '#F5F0FF',
  success: '#008130',
  danger: '#E53E3E',
  grey: '#818181',
  white: '#FFFFFF',
  black: '#000000',
};

const DARK_COLORS: PlanPageThemeColors = {
  primary: '#8B5CF6',
  primaryHover: '#9D71F9',
  primaryPressed: '#7C3AED',
  textDark: '#F4F4F8',
  textSecondary: '#A0A4B8',
  border: '#33354A',
  surface: '#1A1B27',
  background: '#121320',
  activeBg: '#2A2440',
  success: '#27AE60',
  danger: '#FF6B5E',
  grey: '#9A9A9A',
  white: '#FFFFFF',
  black: '#000000',
};

const DEFAULT_THEME_MODES: Record<ThemeMode, PlanPageThemeColors> = {
  [THEME_MODES.LIGHT]: LIGHT_COLORS,
  [THEME_MODES.DARK]: DARK_COLORS,
};

export interface ThemeSliceState {
  mode: ThemeMode;
  header: {
    height: string;
  };
  colors: Record<ThemeMode, PlanPageThemeColors>;
}

const initialState: ThemeSliceState = {
  mode: THEME_MODES.LIGHT,
  header: {
    height: '53px',
  },
  colors: DEFAULT_THEME_MODES,
};

const themeSlice = createSlice({
  name: 'themeSlice',
  initialState,
  reducers: {
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      if (state.colors[action.payload]) {
        state.mode = action.payload;
      }
    },
    toggleThemeMode: (state) => {
      state.mode = state.mode === THEME_MODES.LIGHT ? THEME_MODES.DARK : THEME_MODES.LIGHT;
    },
    /** Registers a new theme mode (e.g. 'high-contrast'); missing tokens fall back to light. */
    registerThemeMode: (
      state,
      action: PayloadAction<{ mode: ThemeMode; colors: Partial<PlanPageThemeColors> }>,
    ) => {
      const { mode, colors } = action.payload;
      state.colors[mode] = { ...LIGHT_COLORS, ...colors };
    },
  },
});

export const { setThemeMode, toggleThemeMode, registerThemeMode } = themeSlice.actions;

export const getThemeMode = (state: any): ThemeMode => state.themeSlice.mode;
export const getAvailableThemeModes = (state: any): ThemeMode[] =>
  Object.keys(state.themeSlice.colors);
export const getThemeHeaderConfig = (state: any): ThemeSliceState['header'] =>
  state.themeSlice.header;
export const getPlanPageThemeColors = (state: any): PlanPageThemeColors =>
  state.themeSlice.colors[state.themeSlice.mode] ?? state.themeSlice.colors[THEME_MODES.LIGHT];

export default themeSlice.reducer;
